/**
 * What a scav SHOUTS when his brain changes state — the AI, read aloud. The
 * server decides when (a transition) and which line (a deterministic index, no
 * rng draw: the scav stream is a replay contract); the client draws the text.
 *
 * Kinds, not sentences, on the wire: a voice line will key off the same enum.
 */

export const BARK_KINDS = ["investigate", "threat", "acquire", "lost", "calm"] as const;

export type BarkKind = (typeof BARK_KINDS)[number];

/** Lines per kind, Tyrolean dialect. At least two of each. */
export const BARK_LINES: Record<BarkKind, readonly string[]> = {
  // A noise, nobody seen.
  investigate: ["Hört's des?", "Wos wor des?", "Do hot wos gscheppert!"],
  // Shot at or hit, the shooter still unseen.
  threat: ["Wer schiaßt do?!", "Auf mi schiaßt er!", "Deckung!"],
  // A target spotted — he is coming.
  acquire: ["Do is er!", "I siach di!", "Kimm her, du Hund!"],
  // Contact lost; the fight became a hunt.
  lost: ["Wo is er hin?", "Wo bist, du Hund?", "Der is weg…"],
  // The sweep is over, back to the patrol.
  calm: ["Nix. Weida.", "War wohl nix.", "Bledsinn…"],
};

/**
 * Which line of `kind` to shout. `salt` is a caller-mixed integer (see
 * `barkSalt`), never a draw — barking must not move the rng schedule.
 */
export function barkVariant(kind: BarkKind, salt: number): number {
  return ((salt | 0) >>> 0) % BARK_LINES[kind].length;
}

/**
 * The line for a variant. Any integer `v` wraps, so a bad wire value still
 * draws something rather than nothing.
 */
export function barkText(kind: BarkKind, v: number): string {
  const lines = BARK_LINES[kind];
  const i = ((v % lines.length) + lines.length) % lines.length;
  return lines[i] as string;
}

export function isBarkKind(x: unknown): x is BarkKind {
  return typeof x === "string" && (BARK_KINDS as readonly string[]).includes(x);
}

/**
 * Tick mixed with a small FNV-1a over the scav's id, so two scavs shouting on
 * the same tick say different things and one scav does not land on the same
 * variant at every multiple of the line count.
 */
export function barkSalt(id: string, tick: number): number {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h + (tick | 0)) | 0;
}
